// Building blocks for the device settings editors, ported from the Claude
// Design "Ruhezeiten Wecker Standby" (variant B) and coloured with the care
// platform's blues. Every colour lives in DS; the round preview's contents are
// what the device shows and keep the device's own colours.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "@fontsource-variable/fraunces";
import { DAY_LABELS, DAY_NAMES, formatSeconds, quietSegments, todayIndex } from "./deviceSettingsModel";

export const SERIF = "'Fraunces Variable', Georgia, serif";

export const DS = {
  accent: "#2563EB", // blue-600, the platform's primary buttons and active tab
  accentSoft: "#DBEAFE",
  accentInk: "#1E40AF",
  ink: "#0F172A",
  muted: "#475569",
  soft: "#64748B",
  night: "#172554", // the dark preview card and rest periods on the 24 h ring
  nightInk: "#F8FAFC",
  nightMuted: "rgba(248,250,252,0.72)",
  nightFaint: "rgba(248,250,252,0.55)",
  card: "#FFFFFF",
  bg: "#F4F8FD", // dialog ground, near the page's #e9f4ff
  field: "#EAF3FF",
  selected: "#EFF6FF",
  ringOff: "#DCE6F2",
  track: "rgba(100,116,139,0.35)",
  line: "rgba(15,23,42,0.08)",
  lineStrong: "rgba(15,23,42,0.16)",
  switchOff: "rgba(15,23,42,0.14)",
  bezel: "linear-gradient(145deg,#E2E8F0,#A3B1C6)",
  danger: "#B91C1C",
};

export function useMinuteClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** One shared player: a second sound stops the first. Same files as the device. */
export function useSoundPreview() {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = (id) => {
    audio.current?.pause();
    if (playing === id) {
      setPlaying(null);
      return;
    }
    const next = new Audio(`/sounds/alarms/${id}.wav`);
    next.onended = () => setPlaying((current) => (current === id ? null : current));
    next.play().catch(() => setPlaying(null));
    audio.current = next;
    setPlaying(id);
  };
  return [playing, toggle];
}

export function Label({ children, className = "" }) {
  return <div className={`px-1 text-xs font-semibold uppercase tracking-[0.12em] ${className}`} style={{ color: DS.soft }}>{children}</div>;
}

export function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
      className="relative h-8 w-[52px] shrink-0 rounded-full p-[3px] transition-colors"
      style={{ background: on ? DS.accent : DS.switchOff }}
    >
      <span
        className="block h-[26px] w-[26px] rounded-full bg-white shadow transition-transform"
        style={{ transform: `translateX(${on ? 20 : 0}px)` }}
      />
    </button>
  );
}

export function DayChips({ days, onChange }) {
  return (
    <div className="flex justify-between gap-1.5">
      {DAY_LABELS.map((label, i) => {
        const on = days.includes(i);
        return (
          <button
            key={label}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? days.filter((d) => d !== i) : [...days, i].sort((a, b) => a - b))}
            className="h-10 w-10 rounded-full text-sm font-semibold"
            style={on ? { background: DS.accent, color: "#fff" } : { border: `1.5px solid ${DS.lineStrong}`, color: DS.ink }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function PlayButton({ playing, onClick, size = 40 }) {
  return (
    <button
      type="button"
      aria-label={playing ? "Stopp" : "Anhören"}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="grid shrink-0 place-items-center rounded-full"
      style={{ width: size, height: size, background: playing ? DS.accent : DS.accentSoft, color: playing ? "#fff" : DS.accentInk }}
    >
      {playing ? (
        <svg viewBox="0 0 24 24" width={size * 0.4} height={size * 0.4}><rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" width={size * 0.4} height={size * 0.4}><polygon points="8 5 19 12 8 19" fill="currentColor" /></svg>
      )}
    </button>
  );
}

export function TimeInput({ value, onChange, big, dark }) {
  return (
    <input
      type="time"
      value={value}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      className="w-full rounded-2xl border-0 text-center outline-none"
      style={{
        fontFamily: SERIF,
        fontSize: big ? 60 : 30,
        fontWeight: 500,
        letterSpacing: -1,
        padding: big ? "10px 18px" : "10px 14px",
        background: dark ? "transparent" : DS.field,
        color: dark ? DS.nightInk : DS.ink,
        colorScheme: dark ? "dark" : "light",
      }}
    />
  );
}

/** Centred dialog with the design's Abbrechen / title / Sichern header.
    Rendered into <body>: the profile page's backdrop-blur container would
    otherwise become the frame for `fixed` and push the dialog's top off screen. */
export function Dialog({ open, title, onCancel, onSave, children, wide }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4" onMouseDown={onCancel}>
      <div
        role="dialog"
        aria-label={title}
        className={`max-h-[90vh] w-full overflow-y-auto rounded-[28px] p-6 shadow-2xl ${wide ? "max-w-2xl" : "max-w-md"}`}
        style={{ background: DS.bg, color: DS.ink }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <button type="button" onClick={onCancel} className="py-2 text-base" style={{ color: DS.muted }}>
            {onSave ? "Abbrechen" : "Schließen"}
          </button>
          <span className="text-lg font-semibold">{title}</span>
          {onSave ? (
            <button type="button" onClick={onSave} className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: DS.accent }}>
              Sichern
            </button>
          ) : <span className="w-16" />}
        </div>
        <div className="flex flex-col gap-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function DangerButton({ children, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 p-3 text-[15px] font-semibold" style={{ color: DS.danger }}>
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" /></svg>
      {children}
    </button>
  );
}

export function AddButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center gap-2 rounded-[20px] p-4 text-base font-semibold"
      style={{ border: `1.5px dashed ${DS.lineStrong}`, color: DS.accent }}
    >
      <span className="text-xl leading-none">+</span>
      {children}
    </button>
  );
}

// ─── Round device preview ───────────────────────────────────────────
/** The rest-period "Gedimmte Uhr": only the time, barely lit. */
function DimClock({ size, now }) {
  return (
    <div className="absolute inset-0 grid place-items-center" style={{ color: "rgba(244,238,229,0.38)" }}>
      <div style={{ fontSize: size * 0.27, fontWeight: 300, letterSpacing: -size * 0.008, lineHeight: 1 }}>
        {String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}
      </div>
    </div>
  );
}

/* Stills of the real device screens (device/frontend: the sleeping SteffiFace
   and the Round Clock faces at 10:10), so the preview is what the resident
   actually sees. The device switches the clock to its dark dial 20:00–07:00. */
const STILLS = "/images/standby";
const isNight = (now) => now.getHours() >= 20 || now.getHours() < 7;

export function RoundPreview({ mode, face = "antik", offAfter = 5, size = 180, now }) {
  let inner = null;
  if (mode === "animation") {
    inner = <img src={`${STILLS}/sleep.png`} alt="" className="absolute inset-0 h-full w-full object-cover" draggable="false" />;
  } else if (mode === "clock") {
    inner = <img src={`${STILLS}/clock-${face}-${isNight(now) ? "dark" : "light"}.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" draggable="false" />;
  } else if (mode === "dim") {
    inner = <DimClock size={size * 0.91} now={now} />;
  } else if (mode === "photos") {
    inner = (
      <div className="absolute inset-0 grid place-items-center" style={{ background: `repeating-linear-gradient(135deg,${DS.field} 0 8px,${DS.accentSoft} 8px 16px)` }}>
        <span className="text-center font-mono" style={{ fontSize: Math.max(9, size * 0.055), color: DS.muted, lineHeight: 1.3 }}>Familien-<br />fotos</span>
      </div>
    );
  } else if (mode === "off" && size > 90) {
    inner = <div className="absolute inset-0 grid place-items-center" style={{ color: "rgba(255,255,255,0.22)", fontSize: size * 0.07 }}>aus nach {formatSeconds(offAfter)}</div>;
  }
  return (
    <div className="shrink-0 rounded-full" style={{ width: size, height: size, padding: size * 0.045, background: DS.bezel, boxShadow: "0 10px 30px rgba(15,23,42,0.18)" }}>
      <div className="relative h-full w-full overflow-hidden rounded-full bg-black">{inner}</div>
    </div>
  );
}

// ─── 24-hour ring ───────────────────────────────────────────────────
export function DayRing({ windows, day, now, size = 250 }) {
  const segs = quietSegments(windows, day);
  const deg = (m) => (m / 1440) * 360;
  const stops = [];
  let cur = 0;
  let total = 0;
  segs.forEach(([a, b]) => {
    if (a > cur) stops.push(`${DS.ringOff} ${deg(cur)}deg ${deg(a)}deg`);
    stops.push(`${DS.night} ${deg(Math.max(a, cur))}deg ${deg(b)}deg`);
    total += Math.max(0, b - Math.max(a, cur));
    cur = Math.max(cur, b);
  });
  if (cur < 1440) stops.push(`${DS.ringOff} ${deg(cur)}deg 360deg`);
  const nowDeg = deg(now.getHours() * 60 + now.getMinutes());
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(${stops.join(",")})` }} />
      <div className="absolute rounded-full" style={{ inset: 26, background: DS.card, boxShadow: "inset 0 2px 6px rgba(15,23,42,0.06)" }} />
      {[0, 6, 12, 18].map((h) => (
        <div key={h} className="absolute" style={{ inset: 30, transform: `rotate(${h * 15}deg)` }}>
          <div className="text-center text-[11px] font-semibold" style={{ color: DS.soft, transform: `rotate(${-h * 15}deg)` }}>{h}</div>
        </div>
      ))}
      {day === todayIndex(now) && (
        <div className="absolute inset-0" style={{ transform: `rotate(${nowDeg}deg)` }}>
          <div className="mx-auto mt-[7px] h-3 w-3 rounded-full border-[2.5px] border-white shadow" style={{ background: DS.accent }} />
        </div>
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={DS.night} strokeWidth="1.7"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /></svg>
        <div className="mt-1" style={{ fontFamily: SERIF, fontSize: 40, fontWeight: 500, lineHeight: 1.1 }}>
          {(total / 60).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Std.
        </div>
        <div className="text-[13px]" style={{ color: DS.muted }}>Ruhe am {DAY_NAMES[day]}</div>
      </div>
    </div>
  );
}
